import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Dashboard.css';

function OvertimeDashboard() {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [unallocatedSlots, setUnallocatedSlots] = useState([]);
  const [allocatedSlots, setAllocatedSlots] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Centralized data fetch function
  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [unallocRes, allocRes, driversRes] = await Promise.all([
        axios.get(`https://anltcs.noorride.com/extra_slots_intervals?dati=${date}`),
        axios.get(`https://anltcs.noorride.com/overtime_slots?date=${date}`),
        axios.get('https://anltcs.noorride.com/drivers')
      ]);
      
      setUnallocatedSlots(unallocRes.data || []);
      setAllocatedSlots(allocRes.data || []);
      setDrivers(driversRes.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all data when date changes
  useEffect(() => {
    fetchAllData();
  }, [date]);

  // Handle slot allocation
  const handleAllocate = async (slot, driverId) => {
    try {
      // Calculate hours
      const start = new Date(slot.start_time);
      const end = new Date(slot.end_time);
      const hours = (end - start) / (1000 * 60 * 60);
      
      // Format payload
      const formatTime = (timeStr) => timeStr.slice(0, 10) + " " + timeStr.slice(10);
      const payload = {
        driver_id: driverId,
        driver_name: drivers.find(d => d.id === driverId)?.name || 'Unknown',
        start_time: formatTime(slot.start_time),
        end_time: formatTime(slot.end_time),
        hours: hours.toFixed(1)
      };

      // Send to backend
      await axios.post('https://anltcs.noorride.com/overtime', payload);
      
      // Refresh data
      await fetchAllData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Allocation failed');
    }
  };

  // Handle unallocation
  const handleUnallocate = async (slotId) => {
    try {
      await axios.delete(`https://anltcs.noorride.com/overtime_slots/${slotId}`);
      // Refresh data
      await fetchAllData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Unallocation failed');
    }
  };

  const calculateTotalUnallocated = () => {
    return unallocatedSlots.reduce((total, slot) => total + (slot.extra_slots_needed || 0), 0);
  };

  return (
    <div className="dashboard-container">
      <header>
        <h1>Overtime Slot Management</h1>
      </header>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : (
        <div className="dashboard-layout">
          {/* Left Sidebar with Date Filter */}
          <div className="sidebar">
            <div className="date-filter-card">
              <h3>Date Filter</h3>
              <div className="date-picker">
                <label>Select Date:</label>
                <input 
                  type="date" 
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="date-input"
                />
              </div>
              <div className="stats-summary">
                <div className="stat-card">
                  <span className="stat-value">{calculateTotalUnallocated()}</span>
                  <span className="stat-label">Unallocated</span>
                </div>
                <div className="stat-card">
                  <span className="stat-value">{allocatedSlots.length}</span>
                  <span className="stat-label">Allocated</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="main-content">
            {/* Unallocated Slots Section */}
            <section className="unallocated-section">
              <div className="section-header">
                <h2>Unallocated Slots</h2>
                <span className="badge">{unallocatedSlots.length}</span>
              </div>
              {unallocatedSlots.length === 0 ? (
                <div className="empty-state">No unallocated slots available</div>
              ) : (
                <div className="slot-grid">
                  {unallocatedSlots.flatMap(slot => 
                    Array.from({ length: slot.extra_slots_needed || 0 }).map((_, i) => {
                      const normalizeTime = (timeStr) => {
                        if (timeStr && timeStr.length === 15 && timeStr[10] !== ' ') {
                          return `${timeStr.slice(0, 10)}T${timeStr.slice(10)}`;
                        }
                        return timeStr;
                      };

                      const startTime = normalizeTime(slot.start_time);
                      const endTime = normalizeTime(slot.end_time);
                      const hours = (new Date(endTime) - new Date(startTime)) / (1000 * 60 * 60);

                      return (
                        <div key={`${slot.interval}-${i}`} className="slot-card unallocated">
                          <div className="slot-header">
                            <h3>{slot.interval || 'No interval'}</h3>
                            <span className="slot-hours">
                              {isNaN(hours) ? 'Invalid time' : `${hours.toFixed(1)} hrs`}
                            </span>
                          </div>
                          <div className="slot-actions">
                            <select 
                              onChange={(e) => handleAllocate(slot, e.target.value)}
                              defaultValue=""
                              className="driver-select"
                            >
                              <option value="" disabled>Select Driver</option>
                              {drivers.map(driver => (
                                <option key={driver.id} value={driver.id}>
                                  {driver.email || 'Unknown driver'}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </section>

            {/* Allocated Slots Section */}
            <section className="allocated-section">
              <div className="section-header">
                <h2>Allocated Slots</h2>
                <span className="badge">{allocatedSlots.length}</span>
              </div>
              {allocatedSlots.length === 0 ? (
                <div className="empty-state">No allocated slots available</div>
              ) : (
                <div className="slot-grid">
                  {allocatedSlots.map(slot => {
                    const interval = slot.interval || 
                      (slot.start_time && slot.end_time 
                        ? `${slot.start_time.slice(-5)} to ${slot.end_time.slice(-5)}` 
                        : 'No interval');

                    return (
                      <div key={slot.id} className="slot-card allocated">
                        <div className="slot-header">
                          <h3>{interval}</h3>
                          <span className="slot-hours">{!isNaN(slot.hours) ? `${slot.hours} hrs` : 'Invalid hours'}</span>
                        </div>
                        <div className="slot-details">
                          <p><strong>Driver:</strong> {drivers.find(d => d.id === slot.driver_id)?.email || 'Unknown'}</p>
                          <p><strong>Date:</strong> {slot.date || 'Unknown date'}</p>
                        </div>
                        <button 
                          className="unassign-btn"
                          onClick={() => handleUnallocate(slot.id)}
                        >
                          Unassign
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        </div>
      )}
   04 </div>
  );
}

export default OvertimeDashboard;